import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import SeoService from '../../../services/SeoService';
import { useBranding } from '../../../context/BrandingContext';

/**
 * Applies backend-managed SEO / GEO / AEO metadata to <head> on every route change.
 * Renders nothing. Skipped on white-label custom-domain portals (BrandingContext).
 */
const upsertMeta = (attr, key, content) => {
  if (content == null || content === '') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute('data-seo', '1');
    document.head.appendChild(el);
  }
  el.setAttribute('content', String(content));
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute('data-seo', '1');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const applyJsonLd = (blocks) => {
  document.head.querySelectorAll('script[data-seo-jsonld]').forEach((n) => n.remove());
  (blocks || []).forEach((block) => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-seo-jsonld', '1');
    s.text = JSON.stringify(block);
    document.head.appendChild(s);
  });
};

let analyticsLoaded = false;
const loadAnalytics = (a) => {
  if (analyticsLoaded || !a) return;
  analyticsLoaded = true;
  if (a.gtm_id) {
    const s = document.createElement('script');
    s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${a.gtm_id}');`;
    document.head.appendChild(s);
  }
  if (a.ga4_id) {
    const g = document.createElement('script');
    g.async = true;
    g.src = `https://www.googletagmanager.com/gtag/js?id=${a.ga4_id}`;
    document.head.appendChild(g);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', a.ga4_id, { send_page_view: false });
  }
  if (a.meta_pixel_id) {
    const p = document.createElement('script');
    p.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${a.meta_pixel_id}');`;
    document.head.appendChild(p);
  }
  if (a.plausible_domain) {
    const pl = document.createElement('script');
    pl.defer = true;
    pl.setAttribute('data-domain', a.plausible_domain);
    pl.src = 'https://plausible.io/js/script.js';
    document.head.appendChild(pl);
  }
};

const trackPageview = (a, url) => {
  if (a?.ga4_id && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', { page_location: url, page_path: window.location.pathname });
  }
  if (a?.meta_pixel_id && typeof window.fbq === 'function') window.fbq('track', 'PageView');
};

const SeoManager = () => {
  const location = useLocation();
  const branding = useBranding();
  const lastPath = useRef(null);

  useEffect(() => {
    if (branding?.custom) return; // white-label portal keeps its own head
    const path = location.pathname || '/';
    if (lastPath.current === path) return;
    lastPath.current = path;

    let cancelled = false;
    SeoService.getMeta(path)
      .then((m) => {
        if (cancelled || !m) return;
        document.title = m.title;
        Object.entries(m.meta || {}).forEach(([k, v]) => upsertMeta('name', k, v));
        Object.entries(m.openGraph || {}).forEach(([k, v]) => upsertMeta('property', k, v));
        Object.entries(m.twitter || {}).forEach(([k, v]) => upsertMeta('name', k, v));
        upsertLink('canonical', m.canonical);
        applyJsonLd(m.jsonLd);
        loadAnalytics(m.analytics);
        trackPageview(m.analytics, m.canonical || window.location.href);
      })
      .catch(() => { /* keep whatever static index.html shipped */ });

    return () => { cancelled = true; };
  }, [location.pathname, branding]);

  return null;
};

export default SeoManager;
